/**
 * Task label colours — the ONE rule shared by the DB CHECK (migration 070), core validation
 * (inventory-core `tasks/labels.ts`) and the web (chips, board bars, /settings/labels).
 *
 * A stored colour is either one of the named tokens below (each has `--tag-<token>-*` CSS
 * variables in apps/web/src/globals.css) or a custom colour stored as lowercase `#rrggbb`.
 * Input accepts `#rgb` / `#rrggbb` in any case and is normalised to lowercase 6-digit hex.
 */

export const LABEL_COLOR_TOKENS = ['gray', 'red', 'orange', 'amber', 'green', 'teal', 'blue', 'indigo', 'purple', 'pink'] as const;
export type LabelColorToken = (typeof LABEL_COLOR_TOKENS)[number];

/** The stored shape of a custom colour (the DB CHECK uses the same pattern). */
export const LABEL_HEX_RE = /^#[0-9a-f]{6}$/;
const HEX_INPUT_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** The rule in words, for error messages and the GraphQL description. */
export const LABEL_COLOR_RULE = `one of ${LABEL_COLOR_TOKENS.join(', ')}, or a hex colour #rgb / #rrggbb`;

export const isLabelColorToken = (c: unknown): c is LabelColorToken =>
  typeof c === 'string' && (LABEL_COLOR_TOKENS as readonly string[]).includes(c);

/** True for a stored (normalised) custom colour: lowercase `#rrggbb`. */
export const isLabelHexColor = (c: unknown): c is string => typeof c === 'string' && LABEL_HEX_RE.test(c);

/** Token → itself; `#abc` / `#AABBCC` → lowercase `#aabbcc`; anything else → null. */
export function normalizeLabelColor(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  if (isLabelColorToken(input)) return input;
  if (!HEX_INPUT_RE.test(input)) return null;
  const h = input.slice(1).toLowerCase();
  return `#${h.length === 3 ? h.replace(/./g, (d) => d + d) : h}`;
}

/** WCAG 2.x relative luminance of a `#rrggbb` (any case). */
export function relativeLuminance(hex: string): number {
  const n = normalizeLabelColor(hex);
  if (!n || !n.startsWith('#')) throw new Error(`not a hex colour: ${hex}`);
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(n.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours (1 … 21). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Text colour for a custom label background: black or white, whichever contrasts more (ties → black). */
export function labelTextColorOn(bg: string): '#000000' | '#ffffff' {
  return contrastRatio(bg, '#000000') >= contrastRatio(bg, '#ffffff') ? '#000000' : '#ffffff';
}
